import React from 'react';

/**
 * Pins the event ID when the event starts (countdown reaches zero) so we don't
 * lose it when the upcoming list refetches and advances.
 * Unpins when the event is no longer started.
 */
export function useLiveEventPin(
  eventStarted: boolean,
  nextEventId: string | number | null | undefined
): {
  liveEventId: string | number | null;
  liveEventIdRef: React.MutableRefObject<string | number | null>;
  setLiveEventIdPinned: React.Dispatch<React.SetStateAction<string | number | null>>;
  resetPin: () => void;
} {
  const [liveEventIdPinned, setLiveEventIdPinned] = React.useState<string | number | null>(null);
  const liveEventIdPinnedRef = React.useRef<string | number | null>(null);

  React.useEffect(() => {
    if (eventStarted && nextEventId != null && liveEventIdPinned == null) {
      liveEventIdPinnedRef.current = nextEventId;
      setLiveEventIdPinned(nextEventId);
    }
    if (!eventStarted && liveEventIdPinned != null) {
      liveEventIdPinnedRef.current = null;
      setLiveEventIdPinned(null);
    }
  }, [eventStarted, nextEventId, liveEventIdPinned]);

  const liveEventId =
    eventStarted && liveEventIdPinnedRef.current != null
      ? liveEventIdPinnedRef.current
      : nextEventId ?? null;

  const resetPin = React.useCallback(() => {
    liveEventIdPinnedRef.current = null;
    setLiveEventIdPinned(null);
  }, []);

  return { liveEventId, liveEventIdRef: liveEventIdPinnedRef, setLiveEventIdPinned, resetPin };
}
